import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { FormProvider, useForm } from 'react-hook-form';
import FormInput from '@/components/forms/FormInput';
import {
  CreateBlindStructureDto,
  GameType,
  getGameTypeInfo,
  LOBBY_ICON_LABELS,
  LobbyIcon,
  PokerVariant,
  TournamentType,
} from '@repo/common-types';
import { toast } from 'react-toastify';
import { ReactComponent as PeopleIcon } from '@/assets/icons/people.svg';
import { ReactComponent as TetherIcon } from '@/assets/tokens/tether-green.svg';
import useTournamentTemplates from '@/hooks/useTournamentTemplates';
import usePayoutStructures from '@/hooks/usePayoutStructures';
import useBlindStructures from '@/hooks/useBlindStructures';
import checkVariants from '@/common/forms/table-handling/checkVariants';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFormStatusReducer } from '@/hooks/useFormStatusReducer';
import useGetPlayerTiers from '@/hooks/useGetPlayerTiers';

// Local enum constants for tournament customization
const TournamentIcon = {
  NONE: 'NONE',
  PHNM_LOGO: 'PHNM_LOGO',
  STAR_LOGO: 'STAR_LOGO',
  BOUNTY_LOGO: 'BOUNTY_LOGO',
  DIAMOND_LOGO: 'DIAMOND_LOGO',
  TICKET_LOGO: 'TICKET_LOGO',
  FREE_LOGO: 'FREE_LOGO',
} as const;

const TournamentColor = {
  DEFAULT_WHITE: 'DEFAULT_WHITE',
  PURPLE: 'PURPLE',
  PINK: 'PINK',
  LIGHT_BLUE: 'LIGHT_BLUE',
  LIGHT_GREEN: 'LIGHT_GREEN',
  ORANGE: 'ORANGE',
  RED: 'RED',
  YELLOW: 'YELLOW',
  WATERMELON: 'WATERMELON',
  LEMON: 'LEMON',
} as const;

interface SnGTemplateFormValues {
  name: string;
  type: TournamentType;
  game: GameType;
  buyIn: number;
  maxPlayers: number;
  payoutStructureId: string;
  tableSize: number;
  startingChips: number;
  blindIntervalInMinutes: number;
  blindStructureId: string;
  startingTimebankInSeconds: number;
  timebankRefillAmountInSeconds: number;
  timebankRefillEveryLevels: number;
  startingDisconnectionTimebankInSeconds: number;
  rake?: number;
  minimumTierRequired?: string | null;
  icon: keyof typeof TournamentIcon;
  titleColor: keyof typeof TournamentColor;
  lobbyIcons: LobbyIcon[];
}

const getValidationSchema = (blindStructures: CreateBlindStructureDto[] | undefined) => {
  return yup.object({
    name: yup.string().required('Name is required').min(1, 'Name is required'),
    type: yup.mixed<TournamentType>().oneOf(Object.values(TournamentType)).required('Type is required'),
    game: yup.mixed<GameType>().oneOf(Object.values(GameType)).required('Game is required'),
    buyIn: yup.number().required('Buy in is required').min(0, 'Buy in must be greater than 0'),
    maxPlayers: yup.number().required('Max players is required').min(1, 'Max players must be greater than 1'),
    rake: yup.number().min(0, 'Rake must be greater than 0'),
    payoutStructureId: yup.string().required('Payout structure is required'),
    tableSize: yup.number().required('Table size is required').min(1, 'Table size must be greater than 1'),
    startingChips: yup.number().required('Starting chips is required').min(0, 'Starting chips must be greater than 0'),
    blindIntervalInMinutes: yup
      .number()
      .required('Blind interval is required')
      .min(0, 'Blind interval must be greater than 0'),
    blindStructureId: yup
      .string()
      .required('Blind structure is required')
      .test(
        'blind-structure-has-bring-ins-if-stud-or-mixed',
        'Stud and mixed games require a blind structure with bring-ins',
        (value, context) => {
          try {
            if (context?.parent?.game && value) {
              const currentBlindStructure = blindStructures?.find((structure) => structure.id === value);
              const { stud, mixed } = checkVariants([context?.parent?.game as PokerVariant]);
              if (stud || mixed) {
                const validBlindStructureForVariant = currentBlindStructure?.levels?.every(
                  (level) => !!level.bringIn && level.bringIn > 0,
                );
                if (!validBlindStructureForVariant) {
                  return false;
                }
              }
            }
            return true;
          } catch (error) {
            console.error('[blind-structure-has-bring-ins-if-stud-or-mixed]', error);
            return false;
          }
        },
      ),
    minimumTierRequired: yup.string().nullable().optional(),
    // Timebank validation fields
    startingTimebankInSeconds: yup
      .number()
      .required('Starting timebank is required')
      .min(10, 'Starting timebank must be at least 10 seconds')
      .max(300, 'Starting timebank cannot exceed 300 seconds (5 minutes)'),
    timebankRefillAmountInSeconds: yup
      .number()
      .required('Timebank refill amount is required')
      .min(10, 'Timebank refill amount must be at least 10 seconds')
      .max(120, 'Timebank refill amount cannot exceed 120 seconds'),
    timebankRefillEveryLevels: yup
      .number()
      .required('Timebank refill frequency is required')
      .min(0, 'Timebank refill frequency must be 0 or greater (0 = disabled)')
      .max(50, 'Timebank refill frequency cannot exceed 50 levels'),
    startingDisconnectionTimebankInSeconds: yup
      .number()
      .required('Starting disconnection timebank is required')
      .min(10, 'Starting disconnection timebank must be at least 10 seconds')
      .max(300, 'Starting disconnection timebank cannot exceed 300 seconds (5 minutes)'),
    icon: yup.string().oneOf(Object.values(TournamentIcon)).required(),
    titleColor: yup.string().oneOf(Object.values(TournamentColor)).required(),
    lobbyIcons: yup
      .array()
      .of(yup.string().oneOf(Object.values(LobbyIcon)).required())
      .default([]),
  });
};

const SnGTemplateModalForm = ({
  onClose,
  selectedTemplateId,
  viewOnly = false,
}: {
  onClose: () => void;
  selectedTemplateId: string | null;
  viewOnly?: boolean;
}) => {
  const { playerTiers } = useGetPlayerTiers();
  const { payoutStructures } = usePayoutStructures();
  const { blindStructures } = useBlindStructures();
  const { tournamentTemplates, createTournamentTemplate, updateTournamentTemplate } = useTournamentTemplates({
    sng: true,
  });

  const selectedTemplate = tournamentTemplates?.find((template) => template.id === selectedTemplateId);

  const [formStatus, dispatchFormStatus] = useFormStatusReducer();
  const [selectedLobbyIcons, setSelectedLobbyIcons] = useState<LobbyIcon[]>([]);

  const enhancedTemplate = selectedTemplate
    ? {
        ...selectedTemplate,
        game: selectedTemplate.game as GameType,
        icon: selectedTemplate.icon ?? TournamentIcon.NONE,
        titleColor: selectedTemplate.titleColor ?? TournamentColor.DEFAULT_WHITE,
        lobbyIcons: selectedTemplate.lobbyIcons as LobbyIcon[],
      }
    : undefined;

  // Memoize validation schema to update when blindStructures changes
  const validationSchema = useMemo(() => getValidationSchema(blindStructures), [blindStructures]);

  const form = useForm<SnGTemplateFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: enhancedTemplate ?? {
      type: TournamentType.SNG,
      // Timebank defaults
      startingTimebankInSeconds: 15,
      timebankRefillAmountInSeconds: 15,
      timebankRefillEveryLevels: 10,
      startingDisconnectionTimebankInSeconds: 30,
      // Tournament customization defaults
      icon: TournamentIcon.NONE,
      titleColor: TournamentColor.DEFAULT_WHITE,
      lobbyIcons: [],
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { isValid, isDirty },
    control,
    watch,
    setValue,
  } = form;

  // Check if lobby icons have changed
  const lobbyIconsChanged = useMemo(() => {
    const originalIcons = selectedTemplate?.lobbyIcons || [];
    return JSON.stringify(selectedLobbyIcons.sort()) !== JSON.stringify(originalIcons.sort());
  }, [selectedLobbyIcons, selectedTemplate]);

  // Combined dirty state including lobby icons
  const hasChanges = isDirty || lobbyIconsChanged;

  // Update form resolver when validation schema changes (when blindStructures loads)
  useEffect(() => {
    form.clearErrors();
    if (Object.keys(form.formState.touchedFields).length > 0) {
      form.trigger();
    }
  }, [validationSchema]);

  useEffect(() => {
    if (selectedTemplate) {
      form.reset(enhancedTemplate);
      setSelectedLobbyIcons(selectedTemplate.lobbyIcons as LobbyIcon[]);
    }
  }, [selectedTemplate, form]);

  const handleLobbyIconToggle = (icon: LobbyIcon) => {
    setSelectedLobbyIcons((prev) => (prev.includes(icon) ? prev.filter((i) => i !== icon) : [...prev, icon]));
  };

  // State for the admin interface (total buy-in and rake amount)
  const [adminTotalBuyIn, setAdminTotalBuyIn] = React.useState(0);
  const [adminRakeAmount, setAdminRakeAmount] = React.useState(0);

  // Initialize admin values from existing template
  useEffect(() => {
    if (selectedTemplate) {
      setAdminTotalBuyIn(selectedTemplate.totalBuyin);
      setAdminRakeAmount(selectedTemplate.rake);
    }
  }, [selectedTemplate]);

  // Tournament customization options
  const iconOptions = [
    { value: TournamentIcon.NONE, label: 'None' },
    { value: TournamentIcon.PHNM_LOGO, label: 'PHNM Logo' },
    { value: TournamentIcon.STAR_LOGO, label: 'Star Logo' },
    { value: TournamentIcon.BOUNTY_LOGO, label: 'Bounty Logo' },
    { value: TournamentIcon.DIAMOND_LOGO, label: 'Diamond Logo' },
    { value: TournamentIcon.TICKET_LOGO, label: 'Ticket Logo' },
    { value: TournamentIcon.FREE_LOGO, label: 'Free Logo' },
  ];

  const colorOptions = [
    { value: TournamentColor.DEFAULT_WHITE, label: 'Default White' },
    { value: TournamentColor.PURPLE, label: 'Purple' },
    { value: TournamentColor.PINK, label: 'Pink' },
    { value: TournamentColor.LIGHT_BLUE, label: 'Light Blue' },
    { value: TournamentColor.LIGHT_GREEN, label: 'Light Green' },
    { value: TournamentColor.ORANGE, label: 'Orange' },
    { value: TournamentColor.RED, label: 'Red' },
    { value: TournamentColor.YELLOW, label: 'Yellow' },
    { value: TournamentColor.WATERMELON, label: 'Watermelon' },
    { value: TournamentColor.LEMON, label: 'Lemon' },
  ];

  // Calculate and update form values when admin inputs change
  useEffect(() => {
    const prizePoolBuyIn = Math.max(0, adminTotalBuyIn - adminRakeAmount);
    setValue('buyIn', prizePoolBuyIn, { shouldDirty: true });
    setValue('rake', adminRakeAmount, { shouldDirty: true });
  }, [adminTotalBuyIn, adminRakeAmount, setValue]);

  const prizePoolBuyIn = Math.max(0, adminTotalBuyIn - adminRakeAmount);

  const game = watch('game');
  const blindStructureId = watch('blindStructureId');

  useEffect(() => {
    if (blindStructureId && (Object.keys(form.formState.dirtyFields).includes('blindStructureId') || blindStructures)) {
      form.trigger('blindStructureId');
    }
  }, [game, blindStructures, blindStructureId, form]);

  const onSubmit = useCallback(
    (data: SnGTemplateFormValues) => {
      const gameTypeInfo = getGameTypeInfo(data.game);
      const transformedData = {
        ...data,
        minPlayers: data.maxPlayers,
        maxPlayers: data.maxPlayers,
        maxAddons: 0,
        maxReentries: 0,
        bountyAmount: 0,
        rake: data.rake ?? 0,
        minimumTierRequired: data.minimumTierRequired ? data.minimumTierRequired : null,
        lobbyIcons: selectedLobbyIcons,
        category: gameTypeInfo.category,
        bettingStructure: gameTypeInfo.bettingStructure.toString(),
      };

      dispatchFormStatus({ type: 'START' });

      if (selectedTemplate?.id) {
        updateTournamentTemplate(
          {
            ...transformedData,
            id: selectedTemplate.id,
          },
          {
            onSuccess: () => {
              onClose();
              dispatchFormStatus({ type: 'SUCCESS' });
              toast.success('Sit&Go template updated successfully');
            },
            onError: () => {
              toast.error('Failed to update Sit&Go template');
              dispatchFormStatus({ type: 'ERROR', error: 'Failed to update SNG template' });
            },
          },
        );
        return;
      } else {
        createTournamentTemplate(transformedData, {
          onSuccess: () => {
            onClose();
            dispatchFormStatus({ type: 'SUCCESS' });
            toast.success('Sit&Go template created successfully');
          },
          onError: () => {
            toast.error('Failed to create Sit&Go template');
            dispatchFormStatus({ type: 'ERROR', error: 'Failed to create SNG template' });
          },
        });
      }
    },
    [
      selectedTemplate,
      updateTournamentTemplate,
      createTournamentTemplate,
      onClose,
      dispatchFormStatus,
      selectedLobbyIcons,
    ],
  );

  return (
    <FormProvider {...form}>
      <Modal isOpen toggle={onClose} id="create-sng-template" className="modal-xl" centered>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            {viewOnly ? 'View' : selectedTemplate?.id ? 'Edit' : 'Create New'} Sit & Go Template
          </ModalHeader>
          <ModalBody style={{ maxHeight: '80vh' }} className="overflow-auto">
            <div className="w-100 d-flex flex-column justify-content-start gap-2">
              <div className="w-100 d-flex justify-content-between flex-row gap-2">
                <Col>
                  <Row className="w-100">
                    <Col>
                      <FormInput control={control} label="Template Name" name="name" disabled={viewOnly} />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="game"
                        label="Game Type"
                        type="select"
                        options={Object.values(GameType).map((type) => ({ value: type, label: type }))}
                        disabled={viewOnly}
                      />
                    </Col>
                  </Row>
                  {/* Modified Buy-in and Rake Section */}
                  <Row className="w-100">
                    <Col>
                      <label className="form-label">Total Buy-In</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <TetherIcon width="16" height="16" />
                        </span>
                        <input
                          type="number"
                          className="form-control"
                          value={adminTotalBuyIn}
                          onChange={(e) => setAdminTotalBuyIn(Number(e.target.value) || 0)}
                          disabled={viewOnly}
                          step="0.01"
                        />
                      </div>
                      {!viewOnly && (
                        <small className="text-muted">Total amount players pay to enter (prize pool + rake)</small>
                      )}
                    </Col>
                    <Col>
                      <label className="form-label">Rake/Fee</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <TetherIcon width="16" height="16" />
                        </span>
                        <input
                          type="number"
                          className="form-control"
                          value={adminRakeAmount}
                          onChange={(e) => setAdminRakeAmount(Number(e.target.value) || 0)}
                          disabled={viewOnly}
                          step="0.01"
                        />
                      </div>
                      {!viewOnly && <small className="text-muted">Fee amount deducted from total buy-in</small>}
                    </Col>
                  </Row>
                  {/* Display calculated values */}
                  {(adminTotalBuyIn > 0 || adminRakeAmount > 0) && (
                    <Row className="w-100 my-2">
                      <Col>
                        <div className="bg-light rounded p-2">
                          <small className="text-muted d-block">Tournament Configuration:</small>
                          <strong>
                            ${prizePoolBuyIn.toFixed(2)} + ${adminRakeAmount.toFixed(2)}
                          </strong>
                          <br />
                          <small className="text-muted">
                            Prize Pool: ${prizePoolBuyIn.toFixed(2)} | Rake: ${adminRakeAmount.toFixed(2)} | Rake %:{' '}
                            {((adminRakeAmount / prizePoolBuyIn) * 100).toFixed(2)}
                          </small>
                        </div>
                      </Col>
                    </Row>
                  )}
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="maxPlayers"
                        label="Players"
                        type="number"
                        allowDecimal={false}
                        Icon={PeopleIcon}
                        disabled={viewOnly}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="payoutStructureId"
                        label="Payout Structure"
                        type="select"
                        options={
                          payoutStructures?.map((structure) => ({ value: structure.id, label: structure.name })) ?? []
                        }
                        disabled={viewOnly}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="tableSize"
                        label="How Many Handed"
                        type="select"
                        valueAsNumber
                        disabled={viewOnly}
                        options={[
                          { value: 2, label: '2 Handed' },
                          { value: 3, label: '3 Handed' },
                          { value: 4, label: '4 Handed' },
                          { value: 5, label: '5 Handed' },
                          { value: 6, label: '6 Handed' },
                          { value: 7, label: '7 Handed' },
                          { value: 8, label: '8 Handed' },
                          { value: 9, label: '9 Handed' },
                        ]}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="startingChips"
                        label="Starting Chips Stack"
                        type="number"
                        allowDecimal={false}
                        disabled={viewOnly}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="blindIntervalInMinutes"
                        label="Blind Duration (mins)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={viewOnly}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="blindStructureId"
                        label="Blind Structure"
                        type="select"
                        options={
                          blindStructures?.map((structure) => ({ value: structure.id, label: structure.name })) ?? []
                        }
                        disabled={viewOnly}
                      />
                    </Col>
                  </Row>
                  {/* Timebank Configuration Section */}
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="startingTimebankInSeconds"
                        label="Starting Timebank (seconds)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={viewOnly}
                        placeholder="15"
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="startingDisconnectionTimebankInSeconds"
                        label="Disconnection Timebank (seconds)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={viewOnly}
                        placeholder="30"
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="timebankRefillAmountInSeconds"
                        label="Timebank Refill Amount (seconds)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={viewOnly}
                        placeholder="30"
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="timebankRefillEveryLevels"
                        label="Refill Every X Levels (0 = disabled)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={viewOnly}
                        placeholder="10"
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormInput
                        control={control}
                        name="minimumTierRequired"
                        label="Minimum Tier Required"
                        type="select"
                        options={playerTiers?.map((tier) => ({
                          value: tier.id,
                          label: tier.name,
                        }))}
                      />
                    </Col>
                    <Col />
                  </Row>

                  {/* Tournament Customization Section */}
                  <Row>
                    <Col>
                      <FormInput
                        control={control}
                        name="icon"
                        label="Tournament Icon"
                        type="select"
                        options={iconOptions}
                        disabled={viewOnly}
                        includeEmptyOption={false}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="titleColor"
                        label="Title Color"
                        type="select"
                        options={colorOptions}
                        disabled={viewOnly}
                        includeEmptyOption={false}
                      />
                    </Col>
                  </Row>

                  {/* Tournament Features Section */}
                  <Row className="w-100 mt-4">
                    <Col>
                      <h5 className="text-primary mb-3">Tournament Features</h5>
                      <div className="bg-light rounded border p-3">
                        <div className="row">
                          {(() => {
                            // Define icons visible only in Tournaments
                            const tournamentOnlyIcons = [LobbyIcon.TURBO, LobbyIcon.HYPER];

                            // Define icons visible in both Tournament & Cash
                            const bothTournamentAndCashIcons = [
                              LobbyIcon.DOUBLE_BOARD,
                              LobbyIcon.BOMB_POT,
                              LobbyIcon.DEEP_STACK,
                              LobbyIcon.ANTE,
                              LobbyIcon.REAL_NAME, // high stakes
                            ];

                            // Get all available tournament icons
                            const availableIcons = [...bothTournamentAndCashIcons, ...tournamentOnlyIcons];

                            return availableIcons.map((icon) => (
                              <div key={icon} className="col-md-4 col-sm-6 mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`lobbyIcon-${icon}`}
                                    checked={selectedLobbyIcons.includes(icon)}
                                    onChange={() => handleLobbyIconToggle(icon)}
                                    disabled={viewOnly}
                                  />
                                  <label className="form-check-label" htmlFor={`lobbyIcon-${icon}`}>
                                    {LOBBY_ICON_LABELS[icon]}
                                  </label>
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="grey" onClick={onClose}>
              Cancel
            </Button>
            {!viewOnly && (
              <Button
                disabled={!isValid || !hasChanges || formStatus.isLoading}
                onClick={handleSubmit(onSubmit)}
                color="primary"
              >
                {formStatus.isLoading
                  ? selectedTemplate?.id
                    ? 'Updating...'
                    : 'Creating...'
                  : selectedTemplate?.id
                    ? 'Update Sit & Go Template'
                    : 'Create New Sit & Go Template'}
              </Button>
            )}
          </ModalFooter>
        </Form>
      </Modal>
    </FormProvider>
  );
};

export default SnGTemplateModalForm;
